// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import loadProductInfoList from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/LoadProductInfoList";
import { getProductsForProductInfoList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import AddToListModal from "@insite/content-library/Components/AddToListModal";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import HomeAccordion from "@steiner/App/Components/Home/HomeAccordion";
import Notify from "@steiner/App/Components/Notify";
import loadReorderPad, { defaultReorderPadParameters } from "@steiner/App/Handlers/ReorderPad/LoadReorderPad";
import { ReorderPadPageDataContext } from "@steiner/App/Pages/ReorderPadPage";
import { CustomAppState } from "@steiner/App/Store/Reducers";
import ReorderPadItem from "@steiner/App/Widgets/ReorderPad/ReorderPadItem";
import DataTable, { DataTableProps, SortOrderOptions } from "@steiner/MobiusOverrides/DataTable";
import { XNGAGE_ELEMENTS } from "@steiner/XDL/constants";
import React, { useContext, useEffect } from "react";
import { FC } from "react";
import { connect, ResolveThunks } from "react-redux";

const labels = {
    productDescription: translate("Product Description"),
    lastOrdered: translate("Last Ordered"),
    lastOrderQTY: translate("Last Order QTY"),
    price: translate("Price"),
    addToCart: translate("Add To Cart"),
};

export interface OwnProps extends WidgetProps { }

const mapStateToProps = (state: CustomAppState, ownProps: OwnProps) => {
    return {
        orders: state.xReorderPad.orders,
        pagination: state.xReorderPad.pagination,
        products: getProductsForProductInfoList(state, ownProps.id),
        isLoading: state.xReorderPad.isLoading,
        searchParams: state.xReorderPad.searchParams,
    };
};

const mapDispatchToProps = {
    loadReorderPad,
    loadProductInfoList,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ReorderPadStyles {
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    notFoundContainer?: InjectableCss;
    notFoundMessage?: TypographyPresentationProps;
    dataTable?: DataTableProps;
    dataTableHead?: DataTableHeadProps;
    dataTableHeaders?: DataTableHeaderProps;
    dataTableBody?: DataTableBodyProps;
}

export const reorderPadStyles: ReorderPadStyles = {};

const styles = reorderPadStyles;
export const ReorderPadTable: FC<Props> = ({
    id: widgetId,
    loadReorderPad,
    loadProductInfoList,
    isLoading,
    searchParams,
    products,
    pagination,
}) => {
    const { orders } = useContext(ReorderPadPageDataContext);
    const sortChangeHandler = (sortField: string) => {
        const currentSort = pagination?.sortType;
        let sort = "";
        if (sortField === "Product Description") {
            sort =
                currentSort === "Product Description (A-Z)" ? "Product Description (Z-A)" : "Product Description (A-Z)";
        } else if (sortField === "Price") {
            sort = currentSort === "Price (A-Z)" ? "Price (Z-A)" : "Price (A-Z)";
        } else if (sortField === "Last Ordered") {
            sort = currentSort === "First Ordered Date" ? "Last Ordered Date" : "First Ordered Date";
        } else if (sortField === "Ordered QTY") {
            sort = currentSort === "Most Ordered QTY" ? "Least Ordered QTY" : "Most Ordered QTY";
        }

        loadReorderPad({
            ...defaultReorderPadParameters,
            ...searchParams,
            sortBy: sort,
        });
    };

    const sorted = (sortField: string) => {
        const currentSort = pagination?.sortType;
        let sorted: boolean | string = false;

        if (currentSort === "Product Description (A-Z)" && sortField === "Product Description") {
            sorted = "ascending";
        } else if (currentSort === "Product Description (Z-A)" && sortField === "Product Description") {
            sorted = "descending";
        } else if (currentSort === "Price (A-Z)" && sortField === "Price") {
            sorted = "ascending";
        } else if (currentSort === "Price (Z-A)" && sortField === "Price") {
            sorted = "descending";
        } else if (currentSort === "First Ordered Date" && sortField === "Last Ordered") {
            sorted = "ascending";
        } else if (currentSort === "Last Ordered Date" && sortField === "Last Ordered") {
            sorted = "descending";
        } else if (currentSort === "Most Ordered QTY" && sortField === "Ordered QTY") {
            sorted = "ascending";
        } else if (currentSort === "Least Ordered QTY" && sortField === "Ordered QTY") {
            sorted = "descending";
        }
        return sorted as SortOrderOptions;
    };

    useEffect(() => {
        if (!orders || orders.length === 0) {
            return;
        }
        const productIds = orders.filter(order => !!order.productDto?.id).map(order => order.productDto!.id);
        loadProductInfoList({
            id: widgetId,
            forceLoad: true,
            getProductCollectionParameter: {
                productIds,
            },
        });
    }, [orders]);

    return (
        <>
            <HomeAccordion
                title={translate("Reorder Pad")}
                isLoading={isLoading}
                pagination={pagination}
                itemsCount={orders?.length || 0}
                showViewAll={false}
            >
                {!orders || orders.length === 0 ? (
                    <>
                        {!isLoading && (
                            <StyledWrapper {...styles.notFoundContainer}>
                                <Notify {...styles.notFoundMessage}>{translate("No items found.")}</Notify>
                            </StyledWrapper>
                        )}
                    </>
                ) : (
                    <DataTable {...styles.dataTable}>
                        <DataTableHead {...styles.dataTableHead}>
                            <DataTableHeader
                                {...styles.dataTableHeaders}
                                onSortClick={() => sortChangeHandler("Product Description")}
                                sorted={sorted("Product Description")}
                            >
                                {labels.productDescription}
                            </DataTableHeader>
                            <DataTableHeader
                                {...styles.dataTableHeaders}
                                onSortClick={() => sortChangeHandler("Last Ordered")}
                                sorted={sorted("Last Ordered")}
                            >
                                {labels.lastOrdered}
                            </DataTableHeader>
                            <DataTableHeader
                                {...styles.dataTableHeaders}
                                onSortClick={() => sortChangeHandler("Ordered QTY")}
                                sorted={sorted("Ordered QTY")}
                            >
                                {labels.lastOrderQTY}
                            </DataTableHeader>
                            <DataTableHeader
                                {...styles.dataTableHeaders}
                                onSortClick={() => sortChangeHandler("Price")}
                                sorted={sorted("Price")}
                            >
                                {labels.price}
                            </DataTableHeader>
                            <DataTableHeader {...styles.dataTableHeaders}>{translate("Add to Cart")}</DataTableHeader>
                        </DataTableHead>
                        <DataTableBody>
                            {orders.map(order => {
                                const product = products?.find(
                                    product => product.productNumber === order.productErpNumber,
                                );
                                return (
                                    <ReorderPadItem
                                        key={order.productErpNumber}
                                        order={order}
                                        product={product}
                                        labels={labels}
                                        widgetId={widgetId}
                                    />
                                );
                            })}
                        </DataTableBody>
                    </DataTable>
                )}
            </HomeAccordion>
            <AddToListModal />
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ReorderPadTable),
    definition: {
        group: XNGAGE_ELEMENTS,
        allowedContexts: ["ReorderPadPage"],
    },
};

export default widgetModule;
